import React, { useEffect, useState } from 'react';
import { ErrorMessage } from '@atlaskit/form';
import InlineEdit from '@atlaskit/inline-edit';
import { type ValueType } from '@atlaskit/select';
import Spinner from '@atlaskit/spinner';
import TextField from '@atlaskit/textfield';
import { invoke, view } from '@forge/bridge';
import { DataNode } from 'rc-tree/lib/interface';

import { CreateIssueMetaResponse } from '../../../../../src/api/issueApi';
import { OptionItemObject, SavingModel } from '../../../../../src/shared_models/interfaces';
import licenseApi from '../../api/licenseApi';
import storageApi from '../../api/storageApi';
import fieldForgeApi, { Field } from '../../bridge/fieldForgeApi';
import { CustomDataNode, LangCodes, LevelsInfo, ViewContext } from '../../interfaces';
import { fetchCustomfieldConfigurationContextId } from '../../service/contextService';
import { Column, SelectDescription, SpinnerArea } from '../../styles';
import { IS_IN_SELECT_CONTEXT, LEVELS_INFO_DEFAULTS, MANUAL_CHANGE, UNDEFINED, VALUE_KEY } from '../../utils/constants';
import {
    findOptionByPropertyAndValue,
    getLevelsInfoData,
    getOptionLabel,
    getTreeData,
    hasValidLength,
    isIssueFieldRequired,
} from '../../utils/levelModelUtils';
import { LicenseBanner } from '../../utils/LicenseBanner';
import { getIssueTypeIdFromTypeId } from '../../utils/utils';

import { FieldSelect, OptionTranslated, SelectTranslated } from './FieldSelect';
import {
    EditCustomFieldWrapperOuter,
    EditViewContainer,
    NoConfigWarning,
    ReadViewContainer,
    ThinBorder,
} from './style';
import { constructViewContext } from './utils';

enum FormWarnings {
    FIELD_REQUIRED_WARNING = 'This field is required. Please choose at least one option.',
    ALL_SELECTS_NOT_FILLED_WARNING = 'All of the selects must be filled.',
}

enum DisplayMode {
    PORTAL = 'portal-create',
    PREVIEW = 'issue-create',
    ISSUE_VIEW = 'issue-view',
    ISSUE_TRANSITION = 'issue-transition',
}

type Props = {
    contextProvider?: () => Promise<ViewContext>;
    customFieldContextId?: string;
    fieldDescription?: string;
    fieldName?: string;
    isPreviewMode?: boolean;
    levelsInfoPreview?: LevelsInfo;
    onSaveOverride?: (model: SavingModel, isValid: boolean) => Promise<void>;
    previewData?: DataNode[];
};

const VALUE_LEVEL = 'value_level';

const defaultContextProvider = async () => constructViewContext(await view.getContext());


export const buildModel = (valuesParam: Map<string, OptionTranslated>, displayMode?: DisplayMode ) => {
    const values = Array.from(valuesParam.values()).filter(val => val);

    if (values.length === 0) {
        return displayMode === DisplayMode.ISSUE_TRANSITION ? null : undefined; // DO NOT REMOVE or issue transition screen will not work
    }

    const configModel: SavingModel = {
        ids: [],
        values: '',
    };

    const model = values.reduce(
        (acc, current, index) => ({
            ...acc,
            ids: acc.ids.concat([current.value]),
            values: acc.values.concat((index === 0 ? '' : ', ') + current.label),
            [VALUE_LEVEL + `${index + 1}`]: current.label,
        }),
        configModel
    );

    const translationsNames: any = new Set(
        values.filter(value => value.translation).flatMap(value => Object.keys(value.translation as any))
    );

    const uniqueTranslations = [...translationsNames];

    uniqueTranslations.forEach((translationsName: LangCodes) => {
        // @ts-ignore
        model[translationsName] = values
            .map(value =>
                value.translation && value.translation[translationsName] ? value.translation[translationsName] : value.label
            )
            .join(', ');
    });
    return model;
};

export const EditMultilevelCustomField: React.FC<Props> = ({
    contextProvider = defaultContextProvider,
    customFieldContextId = '',
    isPreviewMode = false,
    onSaveOverride,
    fieldName,
    fieldDescription,
    previewData = [],
    levelsInfoPreview = LEVELS_INFO_DEFAULTS,
}) => {
    const [displayMode, setDisplayMode] = useState<DisplayMode>();
    const [locale, setLocale] = useState<LangCodes>();
    const [selects, setSelects] = useState<SelectTranslated[]>([]);
    const [selectsValues, setSelectsValues] = useState<Map<string, OptionTranslated>>(new Map<string, OptionTranslated>());

    const [isActive, setIsActive] = useState(false);
    const [noConfig, setNoConfig] = useState(false);
    const [isFetchCompleted, setIsFetchCompleted] = useState(false);
    const [isOnChangeTriggered, setIsOnChangeTriggered] = useState(false);
    const [lastSelectedOptionValue, setLastSelectedOptionValue] = useState('');
    const [levelsInfo, setLevelsInfo] = useState<LevelsInfo>({ ...LEVELS_INFO_DEFAULTS });
    const [isFieldRequired, setIsFieldRequired] = useState(false);
    const [formErrorMessage, setFormErrorMessage] = useState<string | undefined>(undefined);
    const [isAllSelectsRequired, setIsAllSelectsRequired] = useState(false);
    const [editValue, setEditValue] = useState<ValueType<OptionTranslated, true>>([]);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (isOnChangeTriggered) {
            const lastSelectedOption = findOptionByPropertyAndValue(selects as any, VALUE_KEY, lastSelectedOptionValue);
            const latestSelect = selects[selects.length - 1];
            const nextSelectDefaultOption = lastSelectedOption.nextSelect?.options.find(
                (option: OptionTranslated) => option.isEnabled && option.isDefaultOption
            );

            if (nextSelectDefaultOption) {
                handleOnChange(latestSelect)(nextSelectDefaultOption);
            }
        }
    }, [JSON.stringify(selects)]);

    useEffect(() => {
        if (isOnChangeTriggered && fieldIsEmpty()) {
            setDefaultsForEmptyFieldIfAvailable(selects);
        }
    }, [isOnChangeTriggered]);

    const fieldIsEmpty = () => selectsValues.size === 0;

    const setDefaultsForEmptyFieldIfAvailable = (selects: SelectTranslated[]) => {
        if (selects.length > 0) {
            const initialSelect = selects[0];
            const defaultOption = initialSelect.options?.find(option => option.isDefaultOption);

            if (defaultOption) {
                handleOnChange(initialSelect, !MANUAL_CHANGE)(defaultOption);
            }
        }
    };

    const parseSelects = (treeData: CustomDataNode[], locale: LangCodes): SelectTranslated[] => {
        const sel = buildSelect(treeData, 0, locale);
        return sel ? [sel] : [];
    };

    const buildSelect = (
        children: CustomDataNode[] | OptionItemObject | undefined,
        index: number,
        locale: LangCodes
    ): SelectTranslated | undefined => {
        index += 1;
        const nodes =
            children && children.length > 0
                ? children.filter((child: OptionItemObject) => (typeof child.isEnabled === UNDEFINED ? true : child.isEnabled))
                : [];
        const options = mapOptions(nodes, index, locale);
        if (options.length > 0) {
            return {
                name: `_${index}`,
                label: `Level ${index}`,
                options: options,
            };
        }
    };

    const mapOptions = (treeData: CustomDataNode[], index: number, locale: LangCodes): OptionTranslated[] =>
        treeData.map(node => toOption(node, index, locale));

    const toOption = (node: CustomDataNode, index: number, locale: LangCodes): OptionTranslated => ({
        value: String(node.key),
        label: getOptionLabel(node, locale, !IS_IN_SELECT_CONTEXT),
        translation: node.translation,
        isDefaultOption: typeof node.isDefaultOption === 'undefined' ? false : node.isDefaultOption,
        isEnabled: typeof node.isEnabled === 'undefined' ? true : node.isEnabled,
        nextSelect: buildSelect(node.children, index, locale),
    });

    const onChange = (
        newSelectsValues: Map<string, OptionTranslated>,
        chosenSelect: SelectTranslated,
        option: OptionTranslated,
        withPush: boolean
    ) => {
        const selectItem = selects.findIndex(select => select.name === chosenSelect.name);
        const newSelects = selects.slice(0, selectItem + 1);
        const selectToRemove = selects.slice(selectItem + 1);
        selectToRemove.forEach(s => newSelectsValues.delete(s.name));

        withPush && newSelects.push(option.nextSelect!);

        setSelects([...newSelects]);
    };

    const handleOnChange =
        (select: SelectTranslated, manualChange = true) =>
        (option: OptionTranslated) => {
            manualChange && setIsOnChangeTriggered(!!option);
            setLastSelectedOptionValue(option ? option.value : '');
            const newSelectsValues = new Map(selectsValues);

            if (option && option.nextSelect) {
                if (!alreadyRendered(option.nextSelect.name)) {
                    setSelects([...selects, option.nextSelect]);
                } else {
                    onChange(newSelectsValues, select, option, true);
                    // @ts-ignore
                    newSelectsValues.set(option.nextSelect.name, null);
                }
            } else {
                onChange(newSelectsValues, select, option, false);
            }
            newSelectsValues.set(select.name, option);
            setSelectsValues(newSelectsValues);
            setFormErrorMessage(undefined);
        };

    const alreadyRendered = (selectName: string) => selects.some(select => select.name === selectName);

    const searchOptionValue = (
        select: SelectTranslated,
        index: number,
        fieldValue: SavingModel,
        newSelects: SelectTranslated[],
        newSelectsValues: Map<string, OptionTranslated>
    ) => {
        if (fieldValue && fieldValue.ids && fieldValue.ids[index]) {
            const fieldVal = fieldValue.ids[index];
            const foundSelect = selects.find(select => select.name === `_${index}`);

            if (!foundSelect) {
                newSelects.push(select);
            }

            const option = select.options.find(opt => opt.value == fieldVal);

            if (option) {
                newSelectsValues.set(select.name, option);

                if (option.nextSelect) {
                    index = index + 1;
                    searchOptionValue(option.nextSelect, index, fieldValue, newSelects, newSelectsValues);
                }
            }
        }
    };

    const fetchData = async () => {
        const context = await contextProvider();
        const [isLicenseActive, fields, customFieldContextIdParam, issueMetadata]: [
            isActiveValue: boolean,
            fields: Field[],
            contextId: string,
            issueMetadata: CreateIssueMetaResponse | undefined
        ] = await Promise.all([
            licenseApi.getIsLicenseActive(),
            fieldForgeApi.getFields(),
            customFieldContextId ? Promise.resolve(customFieldContextId) : fetchCustomfieldConfigurationContextId(context),
            isPreviewMode
                ? Promise.resolve(undefined)
                : invoke<CreateIssueMetaResponse>('get-issue-create', {
                      projectId: context.extension.project.id,
                      issueTypeId: getIssueTypeIdFromTypeId(context.extension.issue.typeId),
                  }),
        ]);
        setIsActive(isLicenseActive);
        setLocale(context.locale);
        const { fieldValue, fieldId, renderContext } = context.extension;
        setDisplayMode(renderContext as DisplayMode);
        const loadedData = await loadTreeData(fieldId, customFieldContextIdParam);
        const settings = await storageApi.getSettings(fieldId, customFieldContextIdParam);
        const treeData = getTreeData(loadedData);
        handleInitialContainerStyling();
        if (!hasValidLength(treeData)) {
            setNoConfig(true);
            return;
        }

        const selectsValue = parseSelects(treeData, context.locale);
        const newSelects: SelectTranslated[] = [];
        const newSelectsValues = new Map<string, OptionTranslated>();
        searchOptionValue(selectsValue[0], 0, fieldValue, newSelects, newSelectsValues);
        const selects = newSelects.length > 0 ? newSelects : selectsValue;
        setIsFieldRequired(isIssueFieldRequired(fieldId, issueMetadata));
        setLevelsInfo(isPreviewMode ? levelsInfoPreview : getLevelsInfoData(loadedData));
        setIsAllSelectsRequired(settings.isAllSelectsRequired);
        !isPreviewMode && setSelectsValues(() => newSelectsValues);
        setSelects(() => selects);
        setIsOnChangeTriggered(true);
        setIsFetchCompleted(true);
    };

    const loadTreeData = async (fieldId: string, customFieldContextIdParam: string) => {
        if (isPreviewMode) {
            return previewData;
        }
        return await storageApi.getConfig(fieldId, customFieldContextIdParam);
    };

    const handleInitialContainerStyling = () => {
        const body = document.body;
        body.style.height = 'fit-content';
        body.style.overflow = 'visible';
    };

    const isAllSelectsFilled = (selectsValues: Map<string, OptionTranslated>, selects: SelectTranslated[]) =>
        selects.every(select => selectsValues.get(select.name));

    const noop = () => Function.prototype();

    const checkErrors = (): [boolean, FormWarnings | undefined] => {
        const values = Array.from(selectsValues.values()).filter(val => val);

        if (isAllSelectsRequired && !isAllSelectsFilled(selectsValues, selects))
            return [false, FormWarnings.ALL_SELECTS_NOT_FILLED_WARNING];
        if (values.length === 0 && isFieldRequired) return [false, FormWarnings.FIELD_REQUIRED_WARNING];

        return [true, undefined];
    };

    const onSubmit = async () => {
        try {
            const [isValid, error] = checkErrors();

            if (!isValid) {
                setFormErrorMessage(error?.toString());
            }
            const model = buildModel(selectsValues, displayMode);
            if (onSaveOverride) {
                await onSaveOverride(model!, isValid);
            } else if (isValid) {
                await view.submit(model);
            }
        } catch (e) {
            console.error('error during saving', e);
        }
    };

    const isEditModeOnIssueView = displayMode === DisplayMode.ISSUE_VIEW && !isPreviewMode;

    if (noConfig) {
        return (
            <EditCustomFieldWrapperOuter isEditModeOnIssueView={false}>
                <NoConfigWarning>This field is not configured. Contact Jira administrator.</NoConfigWarning>
            </EditCustomFieldWrapperOuter>
        );
    }

    if (!isFetchCompleted) {
        return (
            <EditCustomFieldWrapperOuter isEditModeOnIssueView={false}>
                <SpinnerArea>
                    <Spinner size='medium' />
                </SpinnerArea>
            </EditCustomFieldWrapperOuter>
        );
    }

    return (
        <LicenseBanner isLicenseValid={isActive}>
            <EditCustomFieldWrapperOuter isEditModeOnIssueView={isEditModeOnIssueView}>
                <Column>
                    <InlineEdit<ValueType<OptionTranslated>>
                        defaultValue={editValue}
                        label={displayMode === DisplayMode.PORTAL ? fieldName : null}
                        editView={() => (
                            <EditViewContainer>
                                {selects.map((select, index) => (
                                    <FieldSelect
                                        key={index}
                                        handleOnChange={handleOnChange}
                                        select={select}
                                        selectsValues={selectsValues}
                                        locale={locale}
                                        levelsInfo={levelsInfo}
                                        isLicenseActive={isActive}
                                        appearance={displayMode === DisplayMode.ISSUE_VIEW ? 'subtle' : 'default'}
                                        spacing={displayMode === DisplayMode.ISSUE_VIEW ? 'compact' : 'default'}
                                    />
                                ))}
                            </EditViewContainer>
                        )}
                        readView={() => (
                            <ReadViewContainer>
                                {selects.map((select, index) => {
                                    const value = selectsValues.get(select.name)?.label || '';
                                    return value || index === 0 ?
                                            <TextField
                                                key={index}
                                                value={value}
                                                readOnly
                                                appearance={displayMode === DisplayMode.ISSUE_VIEW ? 'subtle' : 'standard'}
                                                isCompact={displayMode === DisplayMode.ISSUE_VIEW}
                                                // @ts-ignore
                                                // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
                                                css={ThinBorder} //needed for the border to be consistent with jira native fields
                                            />
                                        :   null;
                                })}
                            </ReadViewContainer>
                        )}
                        onConfirm={onSubmit}
                        onCancel={() => {
                            displayMode !== DisplayMode.PREVIEW ? void view.close() : noop();
                        }}
                        startWithEditViewOpen
                        readViewFitContainerWidth
                        hideActionButtons
                    />

                    {fieldDescription && <SelectDescription topMargin='4px'>{fieldDescription}</SelectDescription>}
                    {formErrorMessage && <ErrorMessage>{formErrorMessage}</ErrorMessage>}

                </Column>
            </EditCustomFieldWrapperOuter>
        </LicenseBanner>
    );
};
